<template>
    <b-modal id="dashboardCustomizeModal" title="Customize Dashboard" size="lg" scrollable>
        <div class="dashboard-customize">
            <div class="mb-2">
                <p class="mb-1">
                    Drag and drop cards to reorder them. Unswitch a card to hide it from the dashboard by default.
                </p>
                <b-alert :show="true" class="p-2" style="line-height: 1.5;">
                    <strong>Tip:</strong> use the "All Cards" button on the dashboard to temporarily view all cards,
                    even if they are hidden by default.
                </b-alert>
            </div>
            <hr>
            </hr>

            <b-row>
                <!-- Column 1 -->
                <b-col cols="6">
                    <div class="column-header">
                        <h6 class="mb-3">
                            <i class="fas fa-arrow-left text-muted mr-2"></i>
                            Left Column
                        </h6>
                    </div>

                    <!-- Fixed item: Patient Actions -->
                    <div class="card-item card-fixed mb-2">
                        <div class="card-switch invisible">
                            <!-- Empty space for alignment -->
                        </div>
                        <div class="card-title flex-fill mb-0">
                            <i class="fas fa-lock mr-2"></i>
                            Patient Actions <small>(Always visible)</small>
                        </div>
                    </div>

                    <draggable v-model="column1Cards" :options="dragOptions" class="card-list" @change="onCardMove">
                        <transition-group name="card-transition" tag="div">
                            <div v-for="card in column1Cards" :key="card.id" class="card-item"
                                :class="{ 'card-hidden': !card.visible }">

                                <div class="card-switch">
                                    <b-form-checkbox v-model="card.visible" @change="onVisibilityChange" switch>
                                    </b-form-checkbox>
                                </div>

                                <div class="card-title flex-fill mb-0">
                                    {{ card.title }}
                                </div>
                            </div>
                        </transition-group>
                    </draggable>
                </b-col>

                <!-- Column 2 -->
                <b-col cols="6">
                    <div class="column-header">
                        <h6 class="mb-3">
                            <i class="fas fa-arrow-right text-muted mr-2"></i>
                            Right Column
                        </h6>
                    </div>

                    <!-- Fixed item: Alerts -->
                    <div class="card-item card-fixed mb-2">
                        <div class="card-switch invisible">
                            <!-- Empty space for alignment -->
                        </div>
                        <div class="card-title flex-fill mb-0">
                            <i class="fas fa-lock mr-2"></i>
                            Alerts <small>(Always visible)</small>
                        </div>
                    </div>

                    <draggable v-model="column2Cards" :options="dragOptions" class="card-list" @change="onCardMove">
                        <transition-group name="card-transition" tag="div">
                            <div v-for="card in column2Cards" :key="card.id" class="card-item"
                                :class="{ 'card-hidden': !card.visible }">

                                <div class="card-switch">
                                    <b-form-checkbox v-model="card.visible" @change="onVisibilityChange" switch>
                                    </b-form-checkbox>
                                </div>

                                <div class="card-title flex-fill mb-0">
                                    {{ card.title }}
                                </div>
                            </div>
                        </transition-group>
                    </draggable>
                </b-col>
            </b-row>
        </div>

        <template #modal-footer>
            <div class="w-100 d-flex justify-content-between pt-2">
                <b-button v-if="hasCustomizations" variant="danger" size="sm" @click="resetToDefaults">
                    <i class="fas fa-undo mr-1"></i>
                    Reset to Defaults
                </b-button>
                <div v-else></div>
                <div>
                    <b-button variant="secondary" class="mr-2" @click="cancel">
                        Cancel
                    </b-button>
                    <b-button variant="primary" @click="save" :disabled="saving" class="px-4">
                        <b-spinner v-if="saving" small class="mr-1" />
                        <strong>{{ saving ? 'Saving...' : 'Save' }}</strong>
                    </b-button>
                </div>
            </div>
        </template>
    </b-modal>
</template>

<script lang="ts">
import { Component, Vue, Prop } from "vue-property-decorator";
import draggable from "vuedraggable";
import { toRaw } from 'vue';
import { DashboardCardConfig, DashboardSettings, DEFAULT_DASHBOARD_CARDS } from "@/models/DashboardSettings";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";

@Component({
    components: {
        draggable,
    },
})
export default class DashboardCustomizeModal extends Vue {
    @Prop({ default: () => [] }) cards!: DashboardCardConfig[];

    private localCards: DashboardCardConfig[] = [];
    private saving = false;

    private dragOptions = {
        animation: 150,
        ghostClass: 'card-ghost',
        chosenClass: 'card-chosen',
        dragClass: 'card-drag',
    };

    get column1Cards(): DashboardCardConfig[] {
        return this.localCards
            .filter(card => card.column === 1)
            .sort((a, b) => a.order - b.order);
    }

    set column1Cards(value: DashboardCardConfig[]) {
        value.forEach((card, index) => {
            card.column = 1;
            card.order = index + 1;
        });

        const column2Cards = this.localCards.filter(card => card.column === 2);
        this.localCards = [...value, ...column2Cards];
        this.updateCardsArray();
    }

    get column2Cards(): DashboardCardConfig[] {
        return this.localCards
            .filter(card => card.column === 2)
            .sort((a, b) => a.order - b.order);
    }

    set column2Cards(value: DashboardCardConfig[]) {
        value.forEach((card, index) => {
            card.column = 2;
            card.order = index + 1;
        });

        const column1Cards = this.localCards.filter(card => card.column === 1);
        this.localCards = [...column1Cards, ...value];
        this.updateCardsArray();
    }

    get hasCustomizations(): boolean {
        return !!(this.$root.$data.userSettings['dashboard-layout'] ||
            localStorage.getItem('dashboard-layout'));
    }

    mounted() {
        this.initializeLocalState();
        this.$root.$on('bv::modal::show', (_: any, modalId: string) => {
            if (modalId === 'dashboardCustomizeModal') {
                this.initializeLocalState();
            }
        });
    }

    beforeDestroy() {
        this.$root.$off('bv::modal::show');
    }

    private initializeLocalState() {
        this.localCards = structuredClone(this.cards);
    }

    private updateCardsArray() {
        this.$forceUpdate();
        this.$emit('cards-updated', this.localCards);
    }

    private onCardMove() {
        this.updateCardsArray();
    }

    private onVisibilityChange() {
        this.updateCardsArray();
    }

    private resetToDefaults() {
        this.$bvModal.msgBoxConfirm('Are you sure you want to reset all cards to their default positions and visibility?', {
            title: 'Reset to Defaults',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Reset',
            cancelTitle: 'Cancel',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
        }).then((confirmed: boolean) => {
            if (confirmed) {
                if ((this as any).$deleteUserSetting) {
                    (this as any).$deleteUserSetting('dashboard-layout');
                }

                localStorage.removeItem('dashboard-layout');

                if (this.$root.$data.userSettings['dashboard-layout']) {
                    this.$delete(this.$root.$data.userSettings, 'dashboard-layout');
                }

                this.localCards = structuredClone(DEFAULT_DASHBOARD_CARDS);
                this.updateCardsArray();

                this.$emit('settings-reset');

                this.$bvModal.hide('dashboardCustomizeModal');
                this.$notification(NotificationOptions.success('Dashboard reset to default settings'));
            }
        });
    }

    private cancel() {
        this.$bvModal.hide('dashboardCustomizeModal');
    }

    private async save() {
        this.saving = true;
        try {
            const settings: DashboardSettings = {
                cards: structuredClone(toRaw(this.localCards))
            };

            this.$emit('save-settings', settings);

            this.$bvModal.hide('dashboardCustomizeModal');

            this.$notification(NotificationOptions.success('Dashboard customization saved successfully'));
        } catch (error) {
            console.error('Error saving dashboard settings:', error);
            this.$notification(NotificationOptions.error('Failed to save dashboard settings'));
        } finally {
            this.saving = false;
        }
    }
}
</script>

<style scoped>
.dashboard-customize {
    min-height: 400px;
}

.card-list {
    min-height: 300px;
    border: 2px dashed #dee2e6;
    border-radius: 0.375rem;
    padding: 1rem;
    background-color: #f8f9fa;
}

.card-item {
    display: flex;
    align-items: center;
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 0.375rem;
    padding: 0.75rem;
    margin-bottom: 0.5rem;
    cursor: grab;
    transition: all 0.2s ease;
}

.card-item:active {
    cursor: grabbing;
}

.card-item:hover {
    border-color: #007bff;
    box-shadow: 0 2px 4px rgba(0, 123, 255, 0.1);
}

.card-item.card-hidden {
    opacity: 0.8;
    background-color: #f8f9fa;
}

.card-switch {
    margin-right: 0.75rem;
    cursor: pointer;
}

.card-title {
    font-weight: 500;
}

/* Fixed items styling */
.card-item.card-fixed {
    opacity: 0.6;
    background-color: #f8f9fa;
    cursor: not-allowed;
    border-style: dashed;
    border-color: #ced4da;
}


/* Drag and drop animations */
.card-ghost {
    opacity: 0;
    background: #0250a3 !important;
    color: white;
}

.card-chosen {
    opacity: 1;
    transform: scale(1.02);
}

.card-drag {
    transform: rotate(5deg);
    transition: none;
}

/* Transition animations */
.card-transition-move {
    transition: transform 0.3s;
}

.card-transition-enter-active,
.card-transition-leave-active {
    transition: all 0.3s;
}

.card-transition-enter,
.card-transition-leave-to {
    opacity: 0;
    transform: translateX(30px);
}


/* Responsive adjustments */
@media (max-width: 768px) {
    .card-list {
        padding: 0.5rem;
    }

    .card-item {
        padding: 0.5rem;
        font-size: 0.875rem;
    }
}
</style>
